import { rem } from 'polished'
import React from 'react'
import styled, { css } from 'styled-components'

import GoogleCardImage from './Common/GoogleCardImage'

import ResponsiveImage from 'components/Common/ResponsiveImage'
import { DesktopLayoutSizes } from 'tripPlanner/components/ItemCard/Common/utils'
import { ItemMetadata } from 'tripPlanner/models'
import ItemImagePlaceholder from 'tripPlanner/Tripkit/Common/ItemImagePlaceholder'
import { TripItem } from 'tripPlanner/types/tripItem'
import { getItemPlace } from 'tripPlanner/utils'

const desktopLayout = css`
  aspect-ratio: unset;
  width: 33%;
  min-width: ${rem(125)};
  max-width: ${rem(260)};
`

const BaseContainer = styled.div`
  position: relative;
  align-self: stretch;
  aspect-ratio: 21 / 10;

  ${DesktopLayoutSizes} {
    ${desktopLayout}
  }

  &.mobileImageOnSide {
    ${desktopLayout}
  }

  @media print {
    display: none;
  }
`

const Image = styled.img`
  position: absolute;
  object-fit: cover;
  object-position: center;
  height: 100%;
  width: 100%;
`

const ImageOverlayContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  left: ${rem(12)};
  right: ${rem(12)};
`

interface Props {
  item: TripItem
  name: string
  /** URL of external image */
  imgSrc?: string
  /** ID of image from svc-image */
  imgId?: string
  placeId?: string
  imageOverlayContent?: React.ReactNode
}

export function ItemImageContainer({
  item,
  name,
  imgId,
  imgSrc,
  placeId,
  imageOverlayContent,
}: Props) {
  const Icon = ItemMetadata[item.type].icon
  const place = getItemPlace(item, 'start')

  let imageElement: React.ReactNode
  const placeHolderImage = (
    <ItemImagePlaceholder icon={<Icon size={30} colour="neutral-four" />} />
  )

  if (imgId) {
    // TODO: pass in aspect ratios and widths for xs and sm as props
    const leftImageAspectRatio = '16:9'
    const topImageAspectRatio =
      item.type !== 'NOTE' && item.isBooked ? leftImageAspectRatio : '2.1'
    imageElement = (
      <ResponsiveImage
        fit="center"
        id={imgId}
        mobileAspectRatio={topImageAspectRatio}
        mobileWidth="100%"
        tabletAspectRatio={leftImageAspectRatio}
        tabletWidth="260px"
        desktopAspectRatio={topImageAspectRatio}
        desktopWidth="50%"
        largeDesktopAspectRatio={leftImageAspectRatio}
        largeDesktopWidth="260px"
      />
    )
  } else if (placeId) {
    imageElement = (
      <GoogleCardImage
        placeId={placeId}
        title={name}
        placeholder={placeHolderImage}
      />
    )
  } else if (imgSrc) {
    imageElement = <Image src={imgSrc} />
  } else if (place?.photoUrl) {
    imageElement = <Image src={place.photoUrl} />
  } else {
    imageElement = placeHolderImage
  }

  return (
    <BaseContainer>
      {imageElement}
      {imageOverlayContent && (
        <ImageOverlayContainer>{imageOverlayContent}</ImageOverlayContainer>
      )}
    </BaseContainer>
  )
}
